import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { StyledText, Tag } from '@opentrons/components'

import {
  ABSORBANCE_READER_INITIALIZE,
  ABSORBANCE_READER_LID,
  ABSORBANCE_READER_READ,
} from '../../../constants'
import { StyledTrans } from './StyledTrans'
import styles from './summary.module.css'

import type { RobotState } from '@opentrons/step-generation'
import type { FormData } from '../../../form-types'

interface AbsorbanceReaderSummaryProps {
  currentStep: FormData
  labwareNicknamesById: Record<string, string>
  labwareState: RobotState['labware']
}

export function AbsorbanceReaderSummary(
  props: AbsorbanceReaderSummaryProps
): JSX.Element | null {
  const { t } = useTranslation('protocol_steps')
  const { currentStep, labwareNicknamesById, labwareState } = props
  const {
    moduleId: absorbanceReaderModuleId,
    absorbanceReaderFormType,
    fileName,
    lidOpen,
    mode,
    referenceWavelength,
    referenceWavelengthActive,
    wavelengths,
  } = currentStep
  const labwareOnAbsorbanceReaderId = Object.values(labwareState).find(
    ({ stack }) => stack.includes(absorbanceReaderModuleId as string)
  )?.stack[0] // top element of stack including absorbance reader
  const labwareOnAbsorbanceReaderNickname =
    labwareOnAbsorbanceReaderId != null
      ? labwareNicknamesById[labwareOnAbsorbanceReaderId]
      : null
  let stepSummaryContent: JSX.Element | null = null
  switch (absorbanceReaderFormType) {
    case ABSORBANCE_READER_READ: {
      stepSummaryContent = (
        <StyledTrans
          i18nKey="protocol_steps:absorbance_reader.read"
          tagInfos={[
            {
              text: labwareOnAbsorbanceReaderNickname,
            },
            {
              text: fileName,
            },
          ]}
        />
      )
      break
    }
    case ABSORBANCE_READER_INITIALIZE: {
      const wavelengthsComponents = (
        <div className={styles.absorbance_reader}>
          {wavelengths.map((wavelength: string, index: number) => (
            <Fragment key={`${wavelength}-${index}`}>
              <Tag
                text={`${wavelength} ${t('application:units.nanometer')}`}
                type="default"
              />
              {index < wavelengths.length - 1 ? (
                <StyledText>,</StyledText>
              ) : null}
            </Fragment>
          ))}
        </div>
      )
      if (mode === 'single') {
        const readingTag = {
          text: `${wavelengths[0]}${t('application:units.nanometer')}`,
        }
        stepSummaryContent =
          referenceWavelengthActive === true && referenceWavelength != null ? (
            <StyledTrans
              i18nKey="protocol_steps:absorbance_reader.initialize_single_with_reference"
              tagInfos={[
                readingTag,
                {
                  text: `${referenceWavelength}${t('application:units.nanometer')}`,
                },
              ]}
            />
          ) : (
            <StyledTrans
              i18nKey="protocol_steps:absorbance_reader.initialize_single"
              tagInfos={[readingTag]}
            />
          )
        break
      }
      // mode is multi
      stepSummaryContent = (
        <div className={styles.absorbance_reader}>
          <StyledText desktopStyle="bodyDefaultRegular">
            {t('protocol_steps:absorbance_reader.initialize_multi')}
          </StyledText>
          {wavelengthsComponents}
        </div>
      )
      break
    }
    case ABSORBANCE_READER_LID: {
      stepSummaryContent = (
        <StyledTrans
          i18nKey="protocol_steps:absorbance_reader.initialize_lid"
          tagInfos={[
            {
              text: t(
                `protocol_steps:absorbance_reader.${
                  lidOpen === true ? 'open' : 'closed'
                }`
              ),
            },
          ]}
        />
      )
      break
    }
  }
  return stepSummaryContent
}
